/**
 * Capacity & Expansion — the screen. The constraints and when each binds,
 * the options with their cost per passenger and their start-by year, and
 * the figures entered in the drawer.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useSession } from "../../app/SessionContext";
import {
  answerExpansion,
  buildExpansionDesk,
  EXPANSION_MODEL_PROMPT,
  materialForExpansion,
  startingExpansion,
  type ExpansionDesk,
  type ExpansionEntered,
} from "../../domain/expansion";
import { money } from "../../domain/intelligence";
import type { ProjectRow } from "../../domain/projects";
import { Button, Card, ErrorState, Field, Input, Skeleton } from "../../design/components";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { useFinance } from "./FinanceForm";
import { AskCard, Refs, Tiles, x as s } from "./shared";
import { useEntered } from "./store";
import { useScreenContext } from "../../assistant/screen";
import { OptionFiguresForm } from "../entryForms/OptionFigures";

function useExpansionRecords() {
  const session = useSession();
  const source = session.source;
  const siteId = session.site.id;
  return useQuery({
    queryKey: ["expansion-records", siteId],
    staleTime: 60_000,
    queryFn: async () => {
      const now = new Date();
      const quiet = async <T,>(p: Promise<T>, fb: T) => p.catch(() => fb);
      const [kpis, critical, projects] = await Promise.all([
        quiet(source.listKpis(siteId), []),
        quiet(
          source.criticalPositions(siteId, now.toISOString(), new Date(now.getTime() + 90 * 86_400_000).toISOString()),
          null,
        ),
        quiet(source.listProjects({ siteId }), [] as ProjectRow[]),
      ]);
      return { kpis, critical, projects };
    },
  });
}

export function ExpansionPage() {
  const session = useSession();
  const records = useExpansionRecords();
  const finance = useFinance();
  const entered = useEntered<ExpansionEntered>("executive.expansion", {});
  /* build 6B: the option whose figures are being entered */
  const [editing, setEditing] = useState<string | null>(null);
  const desk = useMemo<ExpansionDesk | null>(() => {
    if (!records.data) return null;
    return buildExpansionDesk({
      now: new Date(),
      siteCode: session.site.code,
      currency: finance.value.currency,
      entered: entered.value,
      kpis: records.data.kpis,
      critical: records.data.critical,
      projects: records.data.projects,
      finance: finance.value,
    });
  }, [records.data, finance.value, entered.value, session.site.code]);
  useScreenContext(
    desk
      ? {
          route: "/capacity-expansion",
          title: "Capacity & Expansion",
          summary: desk.boardLine,
          material: materialForExpansion(desk),
          figures: desk.figures,
          own: (q) => answerExpansion(q, desk),
          prompt: EXPANSION_MODEL_PROMPT,
          offers: [
            { label: "When do we run out of room?", ask: "when do we run out of room?" },
            { label: "Which option first?", ask: "which option should we start first?" },
            { label: "The board line", ask: "give me the board line" },
          ],
        }
      : null,
  );
  return (
    <Page
      eyebrow={t("{site} · Executive", { site: session.site.code })}
      title="Capacity & Expansion"
      actions={
        <Button variant="primary" onClick={() => window.print()}>
          {t("Print")}
        </Button>
      }
    >
      <p className={s.lead}>
        {t(
          "When the airport runs out of capacity, and what each expansion option costs per passenger.",
        )}
      </p>
      {records.isPending && <Skeleton rows={8} />}
      {records.isError && <ErrorState error={records.error} retry={() => void records.refetch()} />}
      {desk && (
        <div className={s.page}>
          <Tiles items={desk.tiles} currency={desk.currency} />
          {entered.savedTo && <p className={s.saved}>{entered.savedTo}</p>}
          <Card
            title="The constraints"
            sub={
              desk.binding
                ? t("{constraint} binds first", { constraint: t(desk.binding.label) })
                : "no constraint entered"
            }
          >
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>{t("constraint")}</th>
                    <th>{t("demand")}</th>
                    <th>{t("capacity")}</th>
                    <th>{t("used")}</th>
                    <th>{t("saturates")}</th>
                    <th>{t("at a third more growth")}</th>
                    <th>{t("at a third less")}</th>
                  </tr>
                </thead>
                <tbody>
                  {desk.constraints.map((c) => (
                    <tr key={c.key} data-testid={`constraint-${c.key}`}>
                      <td>
                        <b>{t(c.label)}</b>
                        {c.assumed && <div className={s.tag}>{t("starting values")}</div>}
                        <Refs list={c.sources} />
                      </td>
                      <td className={s.num}>{c.demand.toLocaleString("en")}</td>
                      <td className={s.num}>
                        {c.capacity.toLocaleString("en")} {t(c.unit)}
                      </td>
                      <td className={s.num}>{c.utilisationPct}%</td>
                      <td className={s.num}>{c.saturationYear ?? t("never")}</td>
                      <td className={s.num}>{c.low ?? t("never")}</td>
                      <td className={s.num}>{c.high ?? t("never")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          <section className={s.cards} aria-label={t("Options")}>
            {desk.options.map((o) => (
              <div
                key={o.key}
                className={`${s.card} ${o.key === desk.recommended ? s.cardOpen : ""}`}
                data-testid={`expansion-${o.key}`}
              >
                <div className={s.cardHead}>
                  {o.key === desk.recommended && <span className={s.tagBlue}>{t("recommended")}</span>}
                  <span className={o.door === "one_way" ? s.tag : s.tagGreen}>
                    {o.door === "one_way" ? t("one-way door") : t("two-way door")}
                  </span>
                  {o.assumed && <span className={s.tag}>{t("starting figures")}</span>}
                  {entered.value.options?.[o.key]?.enabled === false && (
                    <span className={s.tag}>{t("left out of the comparison")}</span>
                  )}
                </div>
                <h3 className={s.cardTitle}>{o.name}</h3>
                <p className={s.cardText}>{o.meaning}</p>
                <div className={s.big}>
                  <span>{money(o.cost, desk.currency)}</span>
                  <span>{t("{n} months", { n: o.months })}</span>
                  <span>
                    {o.costPerPax != null
                      ? t("{amount}/pax", { amount: money(o.costPerPax, desk.currency) })
                      : t("unpriced per pax")}
                  </span>
                </div>
                <dl className={s.kv}>
                  <div>
                    <dt>{t("relieves")}</dt>
                    <dd>{o.relieves.map((r) => t(r)).join(", ")}</dd>
                  </div>
                  <div>
                    <dt>{t("adds")}</dt>
                    <dd>
                      {o.capacityPax ? t("{n} passengers a year", { n: o.capacityPax.toLocaleString("en") }) : ""}
                      {o.capacityPax && o.stands ? ", " : ""}
                      {o.stands ? t("{n} stands", { n: o.stands }) : ""}
                    </dd>
                  </div>
                  <div>
                    <dt>{t("start by")}</dt>
                    <dd>{o.startBy ?? t("no saturation date to work back from")}</dd>
                  </div>
                </dl>
                <p className={s.cardText}>
                  <b>{t("For.")}</b> {o.forCase} <b>{t("Against.")}</b> {o.againstCase}
                </p>
                <Refs list={o.sources} />
                {!entered.loading &&
                  (editing === o.key ? (
                    <OptionFiguresForm
                      option={o}
                      entered={entered.value}
                      onSave={(next) => entered.save(next)}
                      onDone={() => setEditing(null)}
                    />
                  ) : (
                    <Button size="sm" onClick={() => setEditing(o.key)} data-testid={`option-edit-${o.key}`}>
                      {t("Enter this option's figures")}
                    </Button>
                  ))}
              </div>
            ))}
          </section>
          <Card
            title="The reasoning"
            sub="Cost per added passenger"
          >
            <ol className={s.plain}>
              {desk.reasoning.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ol>
          </Card>
          <AskCard
            title="The line for the board"
            sub="Summary for the board, from the airport's own figures"
            board={desk.boardLine}
            material={materialForExpansion(desk)}
            figures={desk.figures}
            own={(q) => answerExpansion(q, desk)}
            prompt={EXPANSION_MODEL_PROMPT}
            placeholder={t("When do we run out of stands? Which option first? What does the pier cost per passenger?")}
          />
          <details className={s.drawer}>
            <summary>{t("Demand and capacity — the figures every year above rests on")}</summary>
            <div className={s.drawerBody}>
              {!entered.loading && <ExpansionForm key={entered.at ?? "seed"} entered={entered.value} onSave={(e) => void entered.save(e)} />}
            </div>
          </details>
          {desk.gaps.length > 0 && (
            <Card title="What this desk does not cover" sub="so the reader knows what is absent">
              <ul className={s.plain}>
                {desk.gaps.map((g) => (
                  <li key={g}>{g}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}
    </Page>
  );
}

function ExpansionForm({ entered, onSave }: { entered: ExpansionEntered; onSave: (e: ExpansionEntered) => void }) {
  const start = startingExpansion();
  const fields: { key: keyof typeof start; label: string }[] = [
    { key: "passengersPerYear", label: "passengers a year" },
    { key: "growthPct", label: "growth % a year" },
    { key: "terminalCapacityPax", label: "terminal design capacity, pax/yr" },
    { key: "contactStands", label: "contact stands" },
    { key: "remoteStands", label: "remote stands" },
    { key: "standsNeededAtCapacity", label: "stands needed at terminal capacity" },
    { key: "runwayMovementsPerHour", label: "declared runway capacity, movements/h" },
    { key: "peakMovementsPerHour", label: "peak-hour movements today" },
  ];
  const [v, setV] = useState<Record<string, string>>(
    Object.fromEntries(fields.map((f) => [f.key, String(entered[f.key] ?? "")])),
  );
  return (
    <Card
      title="Demand and capacity"
      sub="Blank fields use starting values, and results that use them are marked."
    >
      <form
        className={s.form}
        onSubmit={(e) => {
          e.preventDefault();
          const next: ExpansionEntered = { ...entered };
          for (const f of fields)
            (next as Record<string, unknown>)[f.key] = v[f.key] === "" ? undefined : Number(v[f.key]);
          onSave(next);
        }}
      >
        {fields.map((f) => (
          <Field
            key={f.key}
            label={t("{label} (starting {value})", { label: t(f.label), value: start[f.key].toLocaleString("en") })}
          >
            <Input
              type="number"
              min={0}
              value={v[f.key]}
              onChange={(e) => setV({ ...v, [f.key]: e.target.value })}
              aria-label={t(f.label)}
            />
          </Field>
        ))}
        <Button type="submit" variant="primary">
          {t("Save the figures")}
        </Button>
      </form>
    </Card>
  );
}
